import { useState, useCallback, useEffect } from "react"
import { SSE } from "utils/SSE"
import { ASSIGNED, ACTIVE } from "constants/statuses"
import { getLoggerInstance } from "utils/logger"
import { dataService } from "utils/dataService"
import { parseTaskCallData } from "./utils/parseCallData"
import { usePureCallback } from "hooks/usePureCallback"
import { useAlertMessage } from "@cloudbreakus/ui-components"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { readNmsParticipant } from "./nmsParticipantStore"

const logger = getLoggerInstance()

export const LOGIN_DEFAULT_PRESENCE_APPLIED_KEY = "loginDefaultPresenceApplied"

const markLoginDefaultPresenceApplied = () => {
  sessionStorage.setItem(LOGIN_DEFAULT_PRESENCE_APPLIED_KEY, "true")
}

const wasLoginDefaultPresenceApplied = () => Boolean(sessionStorage.getItem(LOGIN_DEFAULT_PRESENCE_APPLIED_KEY))

export const useInitialCallStatus = ({
  userId,
  handleRingingCall,
  setAnsweredCall,
  initializeCallScreen,
  answeredCall,
  SSEopen,
  setNmsParticipantId,
}) => {
  const { showCustomToast } = useAlertMessage()
  const staleAssignedCallThresholdSeconds = useConfigFlag("calls.staleAssignedCallThresholdSeconds")
  const staleAssignedCallThresholdMs = staleAssignedCallThresholdSeconds
    ? staleAssignedCallThresholdSeconds * 1000
    : undefined

  const [initialCallStatus, setInitialCallStatus] = useState(null) // true/false if call active/assigned on login or not.  null if not yet checked
  const [presenceInitiallySet, setPresenceInitiallySet] = useState(false)
  // True once the post-login presence is established (default applied, or a call is
  // already active). Used to suppress the presence display until it settles, so the
  // nav does not flicker through the worker's transient activity on login.
  const [loginPresenceSettled, setLoginPresenceSettled] = useState(false)

  const checkAssignedCall = useCallback(async () => {
    const response = await dataService.Calls.get({ agentId: userId, status: ASSIGNED })
    if (response?.data?.length) {
      const now = Date.now()
      const assignedCall = response.data.find((call) => {
        if (staleAssignedCallThresholdMs === undefined) {
          return true
        }
        const assignTime = new Date(call.assignTime)
        // Only consider calls assigned within the threshold
        return now - assignTime.getTime() <= staleAssignedCallThresholdMs
      })
      if (assignedCall) {
        logger.debug("ringing call found", assignedCall)
        handleRingingCall(parseTaskCallData(assignedCall, userId))
        return true
      } else {
        logger.debug("ringing call assigned too long ago", {
          assignedCalls: response.data,
        })
        return false
      }
    } else {
      logger.debug("no ringing calls")
      return false
    }
  }, [handleRingingCall, userId, staleAssignedCallThresholdMs])

  const checkActiveCall = useCallback(async () => {
    let task
    try {
      const response = await dataService.Calls.get({ agentId: userId, status: ACTIVE })
      if (response?.data?.length) {
        logger.debug("active call found", response.data[0])
        task = parseTaskCallData(response.data[0], userId)
        if (task.interaction.mediaServer === "NMS") {
          // A refresh destroys the running client, so this asks for a new media participant --
          // and the previous one is left open until the media server times it out about forty
          // seconds later, billing twice in the meantime (AE-1352). We know which participant
          // this tab held, so close it deliberately instead of waiting.
          const stale = readNmsParticipant(task.interaction.id)

          const { data } = await dataService.Interactions.reconnect({
            agentId: userId,
            taskId: task.taskId,
            interactionId: task.interaction.id,
            languageId: task.language.id,
          })
          setNmsParticipantId(data.participantId)

          if (stale && stale !== data.participantId) {
            // Best effort: the participant may already have been reaped, and failing to close it
            // only means waiting for the timeout we have anyway.
            dataService.Participant.hangUp({ interactionId: task.interaction.id, participantId: stale }).catch(
              (err) => logger.debug(`Could not close the previous media participant ${stale}`, err)
            )
          }
        }
        initializeCallScreen()
        setAnsweredCall(task)
        return true
      } else {
        logger.debug("no active calls")
        return false
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setNmsParticipantId("NA")
        initializeCallScreen()
        setAnsweredCall(task)
        logger.debug(`room for task ${task} not found - customer likely hung up`)
      } else {
        logger.error("checking for (&/or joining) active calls failed", err)
        throw err
      }
    }
  }, [userId, initializeCallScreen, setAnsweredCall, setNmsParticipantId])

  const checkCallStatus = usePureCallback(async () => {
    if (answeredCall || !userId) {
      return null
    }

    let callStatus

    try {
      callStatus = (await checkActiveCall()) || (await checkAssignedCall())
    } catch (err) {
      logger.error("checking call status failed", err)
      showCustomToast({
        level: "error",
        message: "Checking your current call status failed",
        onRetry: checkCallStatus,
      })
      return
    }

    setInitialCallStatus((initial) => {
      if (initial !== null) {
        return initial
      }
      return callStatus
    })
  })

  useEffect(() => {
    if (SSEopen) {
      SSE.on("connect", checkCallStatus)

      return () => {
        SSE.off("connect", checkCallStatus)
      }
    }
  }, [SSEopen, checkCallStatus])

  useEffect(() => {
    if (presenceInitiallySet || initialCallStatus === null || !SSEopen) {
      return
    }

    // Active/assigned call: settle immediately — do not override presence mid-call.
    if (initialCallStatus) {
      setPresenceInitiallySet(true)
      markLoginDefaultPresenceApplied()
      setLoginPresenceSettled(true)
      return
    }

    // Refresh / later loads in this browser session: leave presence alone (AE-1051).
    if (wasLoginDefaultPresenceApplied()) {
      setPresenceInitiallySet(true)
      setLoginPresenceSettled(true)
      return
    }

    setPresenceInitiallySet(true)

    // First load after login only: API sets Not Ready via login-default (AE-1265).
    // Only mark the session applied on success so a failed call can retry after refresh.
    dataService.Presence.setLoginDefault({ agentId: userId })
      .then(() => {
        markLoginDefaultPresenceApplied()
      })
      .catch((err) => logger.error("Failed to set default presence on login", err))
      .finally(() => {
        setLoginPresenceSettled(true)
      })
  }, [presenceInitiallySet, initialCallStatus, SSEopen, userId])

  return { initialCallStatusChecked: initialCallStatus !== null, loginPresenceSettled }
}
